import * as React from 'react';
import { useEffect, useState } from 'react';
import { Flashbar } from '@amzn/awsui-components-react-v3';

import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { ReviewAgreementTable } from 'src/components/reviews/reviewAgreementTable';
import { ReviewAgreementResourcesTable } from 'src/components/reviews/reviewAgreementResourceTable';
import { ReviewAgreementDataDiffModal } from 'src/components/reviews/reviewAgreementDataDiffModal';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { Link } from 'src/components/common/Link';
import { generateLinkForReviewer } from 'src/commons/common';
import { createReviewDetailsLink } from 'src/routes';
import { ReviewStatus } from 'src/components/reviews/reviewStatus';

export interface AccessReviewCustodianProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  userInfo: any;
}

export const AccessReviewCustodianPage = (props: AccessReviewCustodianProps) => {
  const [allAgreementResources, setAllAgreementResources] = useState([]);
  const [agreementResourceMap, setAgreementResourceMap] = useState(undefined);
  const [selectedAgreementResources, setSelectedAgreementResources] = useState([]);
  const [agreementDifferences, setAgreementDifferences] = useState(undefined);
  const [agreementDifferencesModalVisible, setAgreementDifferencesModalVisible] = useState(false);
  const [agreementValidationResult, setAgreementValidationResult] = useState<string>(undefined);
  const [notifications, setNotifications] = useState([]);
  const [resourceCountNotifications, setResourceCountNotifications] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    if (agreementResourceMap == null) {
      return;
    }

    const agreementCount = agreementResourceMap.size;
    const resourceCount = agreementResourceMap.values().reduce((sum, arr) => sum + arr.length, 0);

    let message = `You have ${resourceCount} ${
      resourceCount == 1 ? 'resource' : 'resources'
    } grouped by ${agreementCount} ${agreementCount == 1 ? 'agreement' : 'agreements'} to review`;

    if (agreementCount == 0 && resourceCount == 0) {
      message = 'No pending reviews currently';
    }

    setResourceCountNotifications([
      {
        type: 'info',
        content: message,
        dismissible: true,
        onDismiss: () => setResourceCountNotifications([]),
      },
    ]);
  }, [agreementResourceMap]);

  const resourceColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'reviewDetail',
      header: 'Review detail',
      cell: (item) => (
        <Link href={createReviewDetailsLink(item.reviewId)} external={true}>
          {item.reviewId}
        </Link>
      ),
      minWidth: 150,
      sortingField: 'reviewDetail',
    },
    {
      id: 'submittedBy',
      header: 'Submitted by',
      cell: (item) => (
        <Link href={generateLinkForReviewer(item.reviewedBy, item.reviewerType)} external={true}>
          {item.reviewedBy}
        </Link>
      ),
      minWidth: 150,
      sortingField: 'submittedBy',
    },
    {
      id: 'submittedAt',
      header: 'Submitted at',
      cell: (item) => item.reviewedAt,
      minWidth: 150,
      sortingField: 'submittedAt',
    },
    {
      id: 'comment',
      header: 'Comment',
      cell: (item) => item.reviewComment,
      minWidth: 150,
      sortingField: 'comment',
    },
    {
      id: 'action',
      header: 'Action',
      cell: (item) => <ReviewStatus status={item.reviewAction} />,
      minWidth: 150,
      sortingField: 'action',
    },
    {
      id: 'agreementOverride',
      header: 'Agreement validation override',
      cell: (item) => (item.overrideAgreementsReason ? `Yes, reason: ${item.overrideAgreementsReason}` : 'No'),
      minWidth: 150,
      sortingField: 'agreementOverride',
    },
    {
      id: 'seasonId',
      header: 'Review season',
      cell: (item) => item.seasonId,
      minWidth: 150,
      sortingField: 'seasonId',
    },
    {
      id: 'namespace',
      header: 'Resource namespace',
      cell: (item) => item.namespace,
      minWidth: 150,
      sortingField: 'namespace',
    },
    {
      id: 'resourceType',
      header: 'Resource type',
      cell: (item) => item.resourceType,
      minWidth: 150,
      sortingField: 'resourceType',
    },
    {
      id: 'resourceId',
      header: 'Resource ID',
      cell: (item) => (
        <Link external={true} href={item.link}>
          {item.name || item.resourceId}
        </Link>
      ),
      minWidth: 150,
      sortingField: 'resourceId',
    },
  ];

  const handleRefresh = async () => {
    if (agreementResourceMap != null) {
      const agreementCount = agreementResourceMap.size;
      const resourceCount = agreementResourceMap.values().reduce((sum, arr) => sum + arr.length, 0);
      setResourceCountNotifications([
        {
          type: 'info',
          content: `You have ${resourceCount} resource(s) grouped by ${agreementCount} agreement(s)`,
          dismissible: true,
          onDismiss: () => setResourceCountNotifications([]),
        },
      ]);
    }
  };

  const closeAgreementDifferenceModal = () => {
    setAgreementDifferencesModalVisible(false);
  };

  return (
    <>
      {agreementResourceMap && <Flashbar items={resourceCountNotifications}></Flashbar>}
      <Flashbar items={notifications}></Flashbar>
      <br />
      <ReviewAgreementTable
        setAllAgreementResources={setAllAgreementResources}
        setAgreementDifferences={setAgreementDifferences}
        setAgreementValidationResult={setAgreementValidationResult}
        setDiffModalVisible={setAgreementDifferencesModalVisible}
        selectedAgreementResources={selectedAgreementResources}
        agreementResourceMap={agreementResourceMap}
        setAgreementResourceMap={setAgreementResourceMap}
        setNotifications={setNotifications}
        loadingReviews={loadingReviews}
        setLoadingReviews={setLoadingReviews}
        resourceColumnDefinitions={resourceColumnDefinitions}
        {...props}
      />
      <br />
      <br />
      {allAgreementResources != null && allAgreementResources.length > 0 && (
        <ReviewAgreementResourcesTable
          agreementDifferences={agreementDifferences}
          setSelectedAgreementResources={setSelectedAgreementResources}
          selectedAgreementResources={selectedAgreementResources}
          allAgreementResources={allAgreementResources}
          loadingReviews={loadingReviews}
          columnDefinitions={resourceColumnDefinitions}
          {...props}
        />
      )}
      <ReviewAgreementDataDiffModal
        visible={agreementDifferencesModalVisible}
        dismiss={closeAgreementDifferenceModal}
        diff={agreementDifferences}
        agreementValidationResult={agreementValidationResult}
        {...props}
      />
    </>
  );
};
